import { useState, useEffect, useCallback } from 'react'
import { getCurrencySymbol } from '../utils/currencyUtils'
import {
	Box,
	Button,
	Drawer,
	TextField,
	Typography,
	Alert,
	Select,
	MenuItem,
	FormControl,
	useMediaQuery,
	useTheme,
	Checkbox,
	FormControlLabel,
	FormGroup,
	IconButton
} from '@mui/material'
import EditIcon from '@mui/icons-material/Edit'
import CloseIcon from '@mui/icons-material/Close'
import { supabase } from '../utils/supabase'
import posthog, { isPostHogConfigured } from '../posthog'
import CategoryEditorPanel from './CategoryEditorPanel'

export const COLOR_OPTIONS = [
	{ value: '', label: 'None', color: '#e0e0e0' },
	{ value: 'red', label: 'Red', color: '#FF6969' },
	{ value: 'blue', label: 'Blue', color: '#3B82F6' },
	{ value: 'green', label: 'Green', color: '#10B981' },
	{ value: 'orange', label: 'Orange', color: '#F97316' },
	{ value: 'purple', label: 'Purple', color: '#A855F7' },
	{ value: 'pink', label: 'Pink', color: '#EC4899' },
	{ value: 'brown', label: 'Brown', color: '#A16207' },
	{ value: 'teal', label: 'Teal', color: '#14B8A6' },
	{ value: 'indigo', label: 'Indigo', color: '#6366F1' },
	{ value: 'lime', label: 'Lime', color: '#84CC16' },
	{ value: 'cyan', label: 'Cyan', color: '#06B6D4' }
]

export default function ServiceEditorPanel({
	open,
	onClose,
	merchant,
	service = null,
	onSaved,
	defaultIsService = true,
	defaultIsAddOn = false
}) {
	const theme = useTheme()
	const isMobile = useMediaQuery(theme.breakpoints.down('md'))
	const isEdit = Boolean(service?.id)

	// Form state
	const [serviceName, setServiceName] = useState('')
	const [appointmentColor, setAppointmentColor] = useState('')
	const [categoryId, setCategoryId] = useState('') // Now stores UUID instead of text
	const [durationMinutes, setDurationMinutes] = useState('')
	const [price, setPrice] = useState('')
	const [description, setDescription] = useState('')
	const [isService, setIsService] = useState(true)
	const [isAddOn, setIsAddOn] = useState(false)

	const [saving, setSaving] = useState(false)
	const [errorMessage, setErrorMessage] = useState('')

	// Load categories from categories table
	const [categories, setCategories] = useState([])
	const [showCategoryEditor, setShowCategoryEditor] = useState(false)

	const loadCategories = useCallback(async () => {
		try {
			const { data, error } = await supabase
				.from('categories')
				.select('id, name')
				.eq('merchant_id', merchant.id)
				.order('name')

			if (error) throw error
			setCategories(data || [])
		} catch (e) {
			console.error('Failed to load categories:', e)
		}
	}, [merchant?.id])

	// Load categories when panel opens
	useEffect(() => {
		if (open && merchant?.id) {
			loadCategories()
		}
	}, [open, merchant?.id, loadCategories])

	// Initialize form when service changes
	useEffect(() => {
		if (open) {
			setServiceName(service?.name || '')
			setAppointmentColor(service?.appointment_color || '')
			setCategoryId(service?.category_id || '')
			setDurationMinutes(service?.duration_minutes?.toString() || '')
			setPrice(service?.price ? (service.price / 100).toFixed(2) : '')
			setDescription(service?.description || '')
			// Use service values if editing, otherwise use defaults based on active tab
			setIsService(service?.is_service !== undefined ? service.is_service : defaultIsService)
			setIsAddOn(service?.is_add_on !== undefined ? service.is_add_on : defaultIsAddOn)
			setErrorMessage('')
		}
	}, [open, service, defaultIsService, defaultIsAddOn])

	const handleClose = () => {
		setServiceName('')
		setAppointmentColor('')
		setCategoryId('')
		setDurationMinutes('')
		setPrice('')
		setDescription('')
		setIsService(defaultIsService)
		setIsAddOn(defaultIsAddOn)
		setErrorMessage('')
		onClose()
	}

	const handleSave = async () => {
		setSaving(true)
		setErrorMessage('')

		try {
			const name = serviceName.trim()
			const duration = parseInt(durationMinutes)
			const priceValue = parseFloat(price)

			if (!name) throw new Error('Service name is required')
			if (!duration || duration < 1) throw new Error('Duration must be at least 1 minute')
			if (isNaN(priceValue) || priceValue < 0) throw new Error('Price must be a valid positive number')
			if (!isService && !isAddOn) throw new Error('Item must be a service, add-on, or both')

			const payload = {
				merchant_id: merchant.id,
				name,
				duration_minutes: duration,
				category_id: categoryId || null,
				appointment_color: appointmentColor.trim() || null,
				price: Math.round(priceValue * 100), // Convert dollars to cents
				description: description.trim() || null,
				is_service: isService,
				is_add_on: isAddOn
			}

			let error
			if (isEdit) {
				({ error } = await supabase
					.from('service_types')
					.update(payload)
					.eq('id', service.id)
					.select('id, name, duration_minutes, category_id, appointment_color, price, description, is_service, is_add_on')
					.single())
			} else {
				({ error } = await supabase
					.from('service_types')
					.insert(payload)
					.select('id, name, duration_minutes, category_id, appointment_color, price, description, is_service, is_add_on')
					.single())
			}

			if (error) throw error

			if (!isEdit && isPostHogConfigured) {
				posthog.capture('service_created', {
					is_service: isService,
					is_add_on: isAddOn,
					duration_minutes: duration,
					price: Math.round(priceValue * 100),
					has_category: Boolean(categoryId),
				})
			}
			onSaved && onSaved()
			handleClose()
		} catch (e) {
			setErrorMessage(e.message || 'Failed to save service')
		} finally {
			setSaving(false)
		}
	}

	const handleDelete = async () => {
		if (!window.confirm('Are you sure you want to delete this service?')) return

		setSaving(true)
		setErrorMessage('')

		try {
			const { error } = await supabase
				.from('service_types')
				.delete()
				.eq('id', service.id)

			if (error) throw error

			onSaved && onSaved()
			handleClose()
		} catch (e) {
			setErrorMessage(e.message || 'Failed to delete service')
		} finally {
			setSaving(false)
		}
	}

	// Render content function
	const renderContent = () => (
		<>
			{/* Mobile Handle */}
			{isMobile && (
				<Box sx={{
					position: 'absolute',
					top: '8px',
					left: '50%',
					transform: 'translateX(-50%)',
					width: '50px',
					height: '6px',
					bgcolor: '#D9D9D9',
					borderRadius: '1000px'
				}} />
			)}

			<Box sx={{
				display: 'flex',
				flexDirection: 'column',
				alignItems: 'center',
				alignSelf: 'stretch',
				gap: isMobile ? '8px' : '8px',
				padding: isMobile ? '24px 16px 44px' : '16px',
				overflow: 'auto',
				flex: 1,
				minHeight: 0
			}}>
				{/* Header */}
				{isMobile ? (
					<Box sx={{
						display: 'flex',
						flexDirection: 'row',
						justifyContent: 'space-between',
						alignItems: 'center',
						alignSelf: 'stretch',
						gap: '10px'
					}}>
						<Button
							onClick={handleClose}
							disabled={saving}
							sx={{
								padding: '12px 16px',
								minWidth: 'auto',
								color: '#9F7AEA',
								fontFamily: 'Inter, sans-serif',
								fontWeight: 600,
								fontSize: '13px',
								lineHeight: '1.21em',
								textTransform: 'none',
								borderRadius: '6px',
								'&:hover': {
									bgcolor: 'transparent'
								}
							}}
						>
							Close
						</Button>
						<Typography sx={{
							fontFamily: 'Inter, sans-serif',
							fontWeight: 600,
							fontSize: '24px',
							lineHeight: '1.21em',
							color: '#2D3748',
							textAlign: 'center',
							flex: 1
						}}>
							{isEdit ? 'Edit service' : 'Add service'}
						</Typography>
						<Button
							onClick={handleSave}
							disabled={saving || !serviceName.trim() || !durationMinutes || !price}
							sx={{
								padding: '12px 16px',
								minWidth: 'auto',
								color: '#9F7AEA',
								fontFamily: 'Inter, sans-serif',
								fontWeight: 600,
								fontSize: '13px',
								lineHeight: '1.21em',
								textTransform: 'none',
								borderRadius: '6px',
								'&:hover': {
									bgcolor: 'transparent'
								}
							}}
						>
							{saving ? 'Saving...' : 'Save'}
						</Button>
					</Box>
				) : (
					<Box sx={{
						display: 'flex',
						flexDirection: 'row',
						justifyContent: 'space-between',
						alignItems: 'center',
						alignSelf: 'stretch',
						gap: '27px'
					}}>
						<Typography sx={{
							fontFamily: 'Roboto, sans-serif',
							fontWeight: 400,
							fontSize: '22px',
							lineHeight: '1.273em',
							color: '#2D3748'
						}}>
							{isEdit ? 'Edit service' : 'Add service'}
						</Typography>
						<IconButton
							onClick={handleClose}
							disabled={saving}
							sx={{
								padding: '12px',
								bgcolor: '#FFFFFF',
								border: '1px solid #D7CBD6',
								borderRadius: '6px',
								'&:hover': {
									bgcolor: '#F7FAFC'
								}
							}}
						>
							<CloseIcon sx={{ width: 16, height: 16, color: '#2D3748' }} />
						</IconButton>
					</Box>
				)}

				{/* Service name */}
				<Box sx={{
					display: 'flex',
					flexDirection: 'column',
					alignSelf: 'stretch',
					gap: '4px'
				}}>
					<Box sx={{
						display: 'flex',
						flexDirection: 'row',
						alignItems: 'center',
						gap: '4px'
					}}>
						<Typography sx={{
							fontFamily: 'Inter, sans-serif',
							fontWeight: 400,
							fontSize: '12px',
							lineHeight: '1.21em',
							letterSpacing: '-2.083%',
							color: '#2D3748'
						}}>
							Service name
						</Typography>
						<Typography sx={{
							fontFamily: 'Inter, sans-serif',
							fontSize: '12px',
							color: '#E53E3E',
							mb: 0.5
						}}>
							*
						</Typography>
					</Box>
					<TextField
						value={serviceName}
						onChange={(e) => setServiceName(e.target.value)}
						placeholder="e.g. Dog Grooming"
						variant="outlined"
						fullWidth
						sx={{
							'& .MuiOutlinedInput-root': {
								bgcolor: '#FFFFFF',
								borderRadius: '6px',
								'& fieldset': {
									borderColor: '#CBD5E0'
								},
								'&:hover fieldset': {
									borderColor: '#CBD5E0'
								},
								'&.Mui-focused fieldset': {
									borderColor: '#CBD5E0'
								}
							},
							'& .MuiOutlinedInput-input': {
								fontFamily: 'Inter, sans-serif',
								fontWeight: 400,
								fontSize: '14px',
								lineHeight: '1.21em',
								letterSpacing: '-1.786%',
								color: '#2D3748',
								padding: '12px 16px',
								'&::placeholder': {
									color: '#CBD5E0',
									opacity: 1
								}
							}
						}}
					/>
				</Box>

				{/* Color and Category Row */}
				<Box sx={{
					display: 'flex',
					flexDirection: 'row',
					justifyContent: 'stretch',
					alignItems: 'stretch',
					alignSelf: 'stretch',
					gap: '18px'
				}}>
					{/* Color */}
					<Box sx={{
						display: 'flex',
						flexDirection: 'column',
						gap: '4px',
						flex: 1
					}}>
						<Typography sx={{
							fontFamily: 'Inter, sans-serif',
							fontWeight: 400,
							fontSize: '12px',
							lineHeight: '1.21em',
							letterSpacing: '-2.083%',
							color: '#2D3748'
						}}>
							Color
						</Typography>
						<FormControl fullWidth>
							<Select
								value={appointmentColor}
								onChange={(e) => setAppointmentColor(e.target.value)}
								displayEmpty
								sx={{
									bgcolor: '#FFFFFF',
									borderRadius: '6px',
									'& .MuiOutlinedInput-notchedOutline': {
										borderColor: '#CBD5E0'
									},
									'&:hover .MuiOutlinedInput-notchedOutline': {
										borderColor: '#CBD5E0'
									},
									'&.Mui-focused .MuiOutlinedInput-notchedOutline': {
										borderColor: '#CBD5E0'
									},
									'& .MuiSelect-select': {
										fontFamily: 'Inter, sans-serif',
										fontWeight: 400,
										fontSize: '14px',
										lineHeight: '1.21em',
										letterSpacing: '-1.786%',
										color: '#2D3748',
										padding: '12px 16px',
										display: 'flex',
										alignItems: 'center',
										gap: '10px'
									}
								}}
								renderValue={(value) => {
									const selectedOption = COLOR_OPTIONS.find(opt => opt.value === value)
									return (
										<Box sx={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
											<Box
												sx={{
													width: '16px',
													height: '16px',
													borderRadius: '2px',
													backgroundColor: selectedOption?.color || '#e0e0e0',
													flexShrink: 0
												}}
											/>
											<Typography sx={{
												fontFamily: 'Inter, sans-serif',
												fontWeight: 400,
												fontSize: '14px',
												lineHeight: '1.21em',
												letterSpacing: '-1.786%',
												color: '#2D3748'
											}}>
												{selectedOption?.label || 'None'}
											</Typography>
										</Box>
									)
								}}
							>
								{COLOR_OPTIONS.map((option) => (
									<MenuItem key={option.value} value={option.value}>
										<Box sx={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
											<Box
												sx={{
													width: '16px',
													height: '16px',
													borderRadius: '2px',
													backgroundColor: option.color,
													flexShrink: 0
												}}
											/>
											{option.label}
										</Box>
									</MenuItem>
								))}
							</Select>
						</FormControl>
					</Box>

					{/* Category */}
					<Box sx={{
						display: 'flex',
						flexDirection: 'column',
						gap: '4px',
						flex: 1
					}}>
						<Typography sx={{
							fontFamily: 'Inter, sans-serif',
							fontWeight: 400,
							fontSize: '12px',
							lineHeight: '1.21em',
							letterSpacing: '-2.083%',
							color: '#2D3748'
						}}>
							Category
						</Typography>
						<FormControl fullWidth>
							<Select
								value={categoryId}
								onChange={(e) => {
									const value = e.target.value
									if (value === 'add-category') {
										setShowCategoryEditor(true)
									} else {
										setCategoryId(value)
									}
								}}
								displayEmpty
								sx={{
									bgcolor: '#FFFFFF',
									borderRadius: '6px',
									'& .MuiOutlinedInput-notchedOutline': {
										borderColor: '#CBD5E0'
									},
									'&:hover .MuiOutlinedInput-notchedOutline': {
										borderColor: '#CBD5E0'
									},
									'&.Mui-focused .MuiOutlinedInput-notchedOutline': {
										borderColor: '#CBD5E0'
									},
									'& .MuiSelect-select': {
										fontFamily: 'Inter, sans-serif',
										fontWeight: 400,
										fontSize: '14px',
										lineHeight: '1.21em',
										letterSpacing: '-1.786%',
										color: '#2D3748',
										padding: '12px 16px'
									}
								}}
								renderValue={(value) => {
									if (!value) {
										return <Typography sx={{
											fontFamily: 'Inter, sans-serif',
											fontWeight: 400,
											fontSize: '14px',
											color: '#CBD5E0'
										}}>Select category</Typography>
									}
									const selectedCategory = categories.find(cat => cat.id === value)
									return selectedCategory?.name || ''
								}}
							>
								<MenuItem value="">
									<em>None</em>
								</MenuItem>
								{categories.map((category) => (
									<MenuItem key={category.id} value={category.id}>
										{category.name}
									</MenuItem>
								))}
								<MenuItem value="add-category">
									<Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#9F7AEA', fontWeight: 600 }}>
										<EditIcon sx={{ width: 16, height: 16 }} />
										<Typography sx={{
											fontFamily: 'Inter, sans-serif',
											fontWeight: 600,
											fontSize: '16px',
											lineHeight: '1.21em',
											color: '#9F7AEA'
										}}>
											Edit Category
										</Typography>
									</Box>
								</MenuItem>
							</Select>
						</FormControl>
					</Box>
				</Box>

				{/* Duration */}
				<Box sx={{
					display: 'flex',
					flexDirection: 'column',
					alignSelf: 'stretch',
					gap: '4px'
				}}>
					<Box sx={{
						display: 'flex',
						flexDirection: 'row',
						alignItems: 'center',
						gap: '4px'
					}}>
						<Typography sx={{
							fontFamily: 'Inter, sans-serif',
							fontWeight: 400,
							fontSize: '12px',
							lineHeight: '1.21em',
							letterSpacing: '-2.083%',
							color: '#2D3748'
						}}>
							Duration (minutes)
						</Typography>
						<Typography sx={{
							fontFamily: 'Inter, sans-serif',
							fontSize: '12px',
							color: '#E53E3E',
							mb: 0.5
						}}>
							*
						</Typography>
					</Box>
					<TextField
						type="number"
						pattern="[0-9]*"
						value={durationMinutes}
						onChange={(e) => setDurationMinutes(e.target.value)}
						placeholder="e.g. 60"
						variant="outlined"
						fullWidth
						inputProps={{ min: 1, step: 1 }}
						sx={{
							'& .MuiOutlinedInput-root': {
								bgcolor: '#FFFFFF',
								borderRadius: '6px',
								'& fieldset': {
									borderColor: '#CBD5E0'
								},
								'&:hover fieldset': {
									borderColor: '#CBD5E0'
								},
								'&.Mui-focused fieldset': {
									borderColor: '#CBD5E0'
								}
							},
							'& .MuiOutlinedInput-input': {
								fontFamily: 'Inter, sans-serif',
								fontWeight: 400,
								fontSize: '14px',
								lineHeight: '1.21em',
								letterSpacing: '-1.786%',
								color: '#2D3748',
								padding: '12px 16px',
								'&::placeholder': {
									color: '#CBD5E0',
									opacity: 1
								}
							}
						}}
					/>
				</Box>

				{/* Price */}
				<Box sx={{
					display: 'flex',
					flexDirection: 'column',
					alignSelf: 'stretch',
					gap: '4px'
				}}>
					<Box sx={{
						display: 'flex',
						flexDirection: 'row',
						alignItems: 'center',
						gap: '4px'
					}}>
						<Typography sx={{
							fontFamily: 'Inter, sans-serif',
							fontWeight: 400,
							fontSize: '12px',
							lineHeight: '1.21em',
							letterSpacing: '-2.083%',
							color: '#2D3748'
						}}>
							Price {getCurrencySymbol(merchant?.currency)}
						</Typography>
						<Typography sx={{
							fontFamily: 'Inter, sans-serif',
							fontSize: '12px',
							color: '#E53E3E',
							mb: 0.5
						}}>
							*
						</Typography>
					</Box>
					<TextField
						type="number"
						value={price}
						pattern="[0-9]*"
						onChange={(e) => setPrice(e.target.value)}
						placeholder="e.g. 50.00"
						variant="outlined"
						fullWidth
						inputProps={{ min: 0, step: 0.01 }}
						sx={{
							'& .MuiOutlinedInput-root': {
								bgcolor: '#FFFFFF',
								borderRadius: '6px',
								'& fieldset': {
									borderColor: '#CBD5E0'
								},
								'&:hover fieldset': {
									borderColor: '#CBD5E0'
								},
								'&.Mui-focused fieldset': {
									borderColor: '#CBD5E0'
								}
							},
							'& .MuiOutlinedInput-input': {
								fontFamily: 'Inter, sans-serif',
								fontWeight: 400,
								fontSize: '14px',
								lineHeight: '1.21em',
								letterSpacing: '-1.786%',
								color: '#2D3748',
								padding: '12px 16px',
								'&::placeholder': {
									color: '#CBD5E0',
									opacity: 1
								}
							}
						}}
					/>
				</Box>

				{/* Description */}
				<Box sx={{
					display: 'flex',
					flexDirection: 'column',
					alignSelf: 'stretch',
					gap: '4px'
				}}>
					<Typography sx={{
						fontFamily: 'Inter, sans-serif',
						fontWeight: 400,
						fontSize: '12px',
						lineHeight: '1.21em',
						letterSpacing: '-2.083%',
						color: '#2D3748'
					}}>
						Description
					</Typography>
					<TextField
						value={description}
						onChange={(e) => setDescription(e.target.value)}
						placeholder="Describe this service..."
						variant="outlined"
						fullWidth
						multiline
						rows={2}
						sx={{
							'& .MuiOutlinedInput-root': {
								bgcolor: '#FFFFFF',
								borderRadius: '6px',
								'& fieldset': {
									borderColor: '#CBD5E0'
								},
								'&:hover fieldset': {
									borderColor: '#CBD5E0'
								},
								'&.Mui-focused fieldset': {
									borderColor: '#CBD5E0'
								}
							},
							'& .MuiOutlinedInput-input': {
								fontFamily: 'Inter, sans-serif',
								fontWeight: 400,
								fontSize: '14px',
								lineHeight: '1.21em',
								letterSpacing: '-1.786%',
								color: '#757575',
								'&::placeholder': {
									color: '#CBD5E0',
									opacity: 1
								}
							}
						}}
					/>
				</Box>

				{/* Type Selection - Service/Add-on */}
				<Box sx={{
					display: 'flex',
					flexDirection: 'row',
					alignItems: 'center',
					alignSelf: 'stretch',
					gap: '16px'
				}}>
					<Typography sx={{
						fontFamily: 'Inter, sans-serif',
						fontWeight: 400,
						fontSize: '12px',
						lineHeight: '1.21em',
						letterSpacing: '-2.083%',
						color: '#2D3748'
					}}>
						Type
					</Typography>
					<FormGroup sx={{ display: 'flex', flexDirection: 'row', gap: '10px' }}>
						<FormControlLabel
							control={
								<Checkbox
									checked={isService}
									onChange={(e) => setIsService(e.target.checked)}
									sx={{
										color: '#CBD5E0',
										'&.Mui-checked': {
											color: '#9F7AEA'
										}
									}}
								/>
							}
							label={
								<Typography sx={{
									fontFamily: 'Inter, sans-serif',
									fontWeight: 400,
									fontSize: '14px',
									lineHeight: '1.21em',
									color: '#2D3748'
								}}>
									Service
								</Typography>
							}
						/>
						<FormControlLabel
							control={
								<Checkbox
									checked={isAddOn}
									onChange={(e) => setIsAddOn(e.target.checked)}
									sx={{
										color: '#CBD5E0',
										'&.Mui-checked': {
											color: '#9F7AEA'
										}
									}}
								/>
							}
							label={
								<Typography sx={{
									fontFamily: 'Inter, sans-serif',
									fontWeight: 400,
									fontSize: '14px',
									lineHeight: '1.21em',
									color: '#2D3748'
								}}>
									Add-on
								</Typography>
							}
						/>
					</FormGroup>
				</Box>

				{/* Error message */}
				{errorMessage && (
					<Alert severity="error" sx={{ width: '100%' }}>
						{errorMessage}
					</Alert>
				)}

				{/* Spacer to push buttons to bottom */}
				<Box sx={{ flexGrow: 1 }} />

				{/* Action buttons */}
				{isMobile ? (
					// Mobile: Only show Delete button at bottom if editing
					isEdit && (
						<Button
							onClick={handleDelete}
							disabled={saving}
							fullWidth
							startIcon={
								<svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
									<path d="M2 4H3.33333H14" stroke="currentColor" strokeWidth="1.33333" strokeLinecap="round" strokeLinejoin="round" />
									<path d="M5.33331 4.00001V2.66668C5.33331 2.31305 5.47379 1.97392 5.72384 1.72387C5.97389 1.47382 6.31302 1.33334 6.66665 1.33334H9.33331C9.68694 1.33334 10.0261 1.47382 10.2761 1.72387C10.5262 1.97392 10.6666 2.31305 10.6666 2.66668V4.00001M12.6666 4.00001V13.3333C12.6666 13.687 12.5262 14.0261 12.2761 14.2762C12.0261 14.5262 11.6869 14.6667 11.3333 14.6667H4.66665C4.31302 14.6667 3.97389 14.5262 3.72384 14.2762C3.47379 14.0261 3.33331 13.687 3.33331 13.3333V4.00001H12.6666Z" stroke="currentColor" strokeWidth="1.33333" strokeLinecap="round" strokeLinejoin="round" />
								</svg>
							}
							sx={{
								alignSelf: 'stretch',
								padding: '12px 16px',
								bgcolor: '#FED7D7',
								border: '1px solid #C53030',
								borderRadius: '6px',
								color: '#C53030',
								fontFamily: 'Inter, sans-serif',
								fontWeight: 600,
								fontSize: '14px',
								lineHeight: '1.21em',
								letterSpacing: '-1.786%',
								textTransform: 'none',
								gap: '10px',
								'&:hover': {
									bgcolor: '#FEC5C5'
								}
							}}
						>
							Delete service
						</Button>
					)
				) : (
					// Desktop: Show Delete and Save buttons
					<Box sx={{
						display: 'flex',
						flexDirection: 'column',
						justifyContent: 'stretch',
						alignItems: 'stretch',
						alignSelf: 'stretch',
						gap: '10px'
					}}>
						<Box sx={{
							display: 'flex',
							flexDirection: 'row',
							justifyContent: 'stretch',
							alignItems: 'stretch',
							alignSelf: 'stretch',
							gap: '10px'
						}}>
							{isEdit && (
								<Button
									onClick={handleDelete}
									disabled={saving}
									fullWidth
									sx={{
										padding: '12px 16px',
										border: '1px solid #BAB8B8',
										borderRadius: '6px',
										color: '#2D3748',
										fontFamily: 'Inter, sans-serif',
										fontWeight: 600,
										fontSize: '14px',
										lineHeight: '1.21em',
										letterSpacing: '-1.786%',
										textTransform: 'none',
										'&:hover': {
											border: '1px solid #BAB8B8'
										}
									}}
								>
									Delete
								</Button>
							)}
							<Button
								onClick={handleSave}
								disabled={saving || !serviceName.trim() || !durationMinutes || !price}
								fullWidth
								sx={{
									padding: '12px 16px',
									bgcolor: '#E9D8FD',
									border: '1px solid #9F7AEA',
									borderRadius: '6px',
									color: '#2D3748',
									fontFamily: 'Inter, sans-serif',
									fontWeight: 600,
									fontSize: '14px',
									lineHeight: '1.21em',
									letterSpacing: '-1.786%',
									textTransform: 'none',
									'&:hover': {
										bgcolor: '#D6BCFA'
									}
								}}
							>
								{saving ? 'Saving...' : 'Save'}
							</Button>
						</Box>
					</Box>
				)}
			</Box>
		</>
	)

	// Category Editor Panel (rendered for both desktop and mobile)
	const categoryEditorPanel = (
		<CategoryEditorPanel
			open={showCategoryEditor}
			onClose={() => setShowCategoryEditor(false)}
			merchant={merchant}
			onSaved={() => {
				loadCategories() // Reload categories after saving
				setShowCategoryEditor(false)
			}}
		/>
	)

	// Desktop: Render as regular Box
	if (!isMobile) {
		if (!open) return null

		return (
			<>
				<Box sx={{
					width: '100%',
					height: '100%',
					bgcolor: '#FFFFFF',
					boxShadow: '0px 4px 4px -4px rgba(12, 12, 13, 0.05), 0px 16px 32px -4px rgba(12, 12, 13, 0.1)',
					borderRadius: '12px 0 0 12px',
					display: 'flex',
					flexDirection: 'column',
					overflow: 'hidden'
				}}>
					{renderContent()}
				</Box>
				{categoryEditorPanel}
			</>
		)
	}

	// Mobile: Use Drawer
	return (
		<>
			<Drawer
				anchor="bottom"
				open={open}
				onClose={handleClose}
				PaperProps={{
					sx: {
						width: '100%',
						height: '90vh',
						maxHeight: '90vh',
						bgcolor: '#F9FAFB',
						boxShadow: '0px -4px 10px 0px rgba(0, 0, 0, 0.06)',
						borderRadius: '20px 20px 0 0'
					}
				}}
			>
				{renderContent()}
			</Drawer>
			{categoryEditorPanel}
		</>
	)
}

